import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth, formatApiError } from "./AuthContext";
import { Plus, Trash2, ExternalLink } from "lucide-react";

export default function AdminArticles() {
  const { API, authHeaders } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmSlug, setConfirmSlug] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/articles`);
      setArticles(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (slug) => {
    setError("");
    try {
      await axios.delete(`${API}/admin/articles/${slug}`, { headers: authHeaders });
      setConfirmSlug(null);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex items-end justify-between gap-6 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-primary">Journal</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-light">Articles</h1>
        </div>
        <Link
          to="/admin/articles/new"
          data-testid="new-article-button"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground text-sm hover:bg-primary/90"
        >
          <Plus size={16} strokeWidth={1.5} />
          New essay
        </Link>
      </div>

      {error && (
        <p className="mb-6 text-sm italic font-serif text-destructive">{error}</p>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : articles.length === 0 ? (
        <p className="text-sm italic font-serif text-muted-foreground">
          No essays yet. Start one.
        </p>
      ) : (
        <div className="bg-white/70 border border-border/60 rounded-3xl overflow-hidden">
          {articles.map((a, i) => (
            <div
              key={a.slug}
              data-testid={`article-row-${a.slug}`}
              className={`flex items-center gap-4 p-5 sm:p-6 ${
                i > 0 ? "border-t border-border/60" : ""
              }`}
            >
              <img
                src={a.image}
                alt=""
                className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg truncate">{a.title}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  {a.category} · {a.read_time}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`/journal/${a.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="p-2 text-foreground/60 hover:text-primary"
                  title="View on site"
                >
                  <ExternalLink size={16} strokeWidth={1.5} />
                </a>
                <Link
                  to={`/admin/articles/${a.slug}/edit`}
                  data-testid={`edit-${a.slug}`}
                  className="text-xs uppercase tracking-[0.2em] text-foreground/70 hover:text-primary px-3"
                >
                  Edit
                </Link>
                <button
                  data-testid={`delete-${a.slug}`}
                  onClick={() => setConfirmSlug(a.slug)}
                  className="p-2 text-foreground/60 hover:text-destructive"
                  title="Delete"
                >
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm delete */}
      {confirmSlug && (
        <div
          data-testid="confirm-delete-modal"
          className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setConfirmSlug(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-background border border-border rounded-3xl p-8 max-w-md w-full"
          >
            <h3 className="font-serif text-2xl font-light">Delete essay?</h3>
            <p className="mt-3 text-sm text-muted-foreground">
              This will permanently remove "{confirmSlug}". This cannot be undone.
            </p>
            <div className="mt-8 flex items-center justify-end gap-4">
              <button
                onClick={() => setConfirmSlug(null)}
                className="text-sm tracking-wide text-foreground/70 hover:text-foreground px-4 py-2"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-delete"
                onClick={() => onDelete(confirmSlug)}
                className="px-6 py-2 rounded-full bg-destructive text-destructive-foreground text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
